import React from 'react';
import styled from 'styled-components';
import { Shield, Activity, Server, Database, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useQuery } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.lg};
`;

const SettingsCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const ServiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ServiceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const ServiceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const ServiceName = styled.div`
  font-weight: 500;
  color: ${props => props.theme.colors.text};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.bg};
  color: ${props => props.color};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const LoadingState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const InfoSection = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div`
  color: ${props => props.theme.colors.textSecondary};
`;

const InfoValue = styled.div`
  font-weight: 500;
  color: ${props => props.theme.colors.text};
`;

function Settings() {
  const { data: healthData, isLoading } = useQuery(
    'systemHealth',
    () => adminAPI.getSystemHealth(),
    {
      refetchInterval: 60000,
      onError: (err) => {
        console.error('System health error:', err);
      }
    }
  );

  const getStatusBadge = (status) => {
    const isUp = status === 'up';
    return (
      <StatusBadge bg={isUp ? '#d1fae5' : '#fee2e2'} color={isUp ? '#059669' : '#dc2626'}>
        {isUp ? <CheckCircle size={14} /> : <XCircle size={14} />}
        {isUp ? 'Работает' : 'Остановлен'}
      </StatusBadge>
    );
  };

  if (isLoading) {
    return (
      <Container>
        <Header>
          <Title>
            <Shield size={32} />
            Настройки
          </Title>
        </Header>
        <LoadingState>Загрузка...</LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <Shield size={32} />
          Настройки
        </Title>
      </Header>

      <SettingsGrid>
        <SettingsCard>
          <CardHeader>
            <Activity size={24} color="#6366f1" />
            <CardTitle>Состояние системы</CardTitle>
          </CardHeader>
          
          {healthData?.data ? (
            <>
              <ServiceList>
                <ServiceItem>
                  <ServiceInfo>
                    <Database size={20} />
                    <ServiceName>PostgreSQL</ServiceName>
                  </ServiceInfo>
                  {getStatusBadge(healthData.data.services?.database)}
                </ServiceItem>
                <ServiceItem>
                  <ServiceInfo>
                    <Server size={20} />
                    <ServiceName>Redis</ServiceName>
                  </ServiceInfo>
                  {getStatusBadge(healthData.data.services?.redis)}
                </ServiceItem>
                <ServiceItem>
                  <ServiceInfo>
                    <Server size={20} />
                    <ServiceName>MinIO</ServiceName>
                  </ServiceInfo>
                  {getStatusBadge(healthData.data.services?.minio)}
                </ServiceItem>
                <ServiceItem>
                  <ServiceInfo>
                    <Server size={20} />
                    <ServiceName>API Сервер</ServiceName>
                  </ServiceInfo>
                  {getStatusBadge(healthData.data.services?.api)}
                </ServiceItem>
                <ServiceItem>
                  <ServiceInfo>
                    <Server size={20} />
                    <ServiceName>Telegram Bot</ServiceName>
                  </ServiceInfo>
                  {getStatusBadge(healthData.data.services?.bot)}
                </ServiceItem>
                <ServiceItem>
                  <ServiceInfo>
                    <Server size={20} />
                    <ServiceName>Worker</ServiceName>
                  </ServiceInfo>
                  {getStatusBadge(healthData.data.services?.worker)}
                </ServiceItem>
              </ServiceList>

              <InfoSection>
                <InfoRow>
                  <InfoLabel>Статус системы</InfoLabel>
                  <StatusBadge bg="#d1fae5" color="#059669">
                    {healthData.data.status || 'healthy'}
                  </StatusBadge>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Время работы</InfoLabel>
                  <InfoValue>{healthData.data.uptime || '99.9%'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Последняя проверка</InfoLabel>
                  <InfoValue>
                    {healthData.data.last_check 
                      ? new Date(healthData.data.last_check).toLocaleString('ru-RU')
                      : '-'}
                  </InfoValue>
                </InfoRow>
              </InfoSection>
            </>
          ) : (
            <div style={{ color: '#6b7280' }}>Данные недоступны</div>
          )}
        </SettingsCard>
      </SettingsGrid>
    </Container>
  );
}

export default Settings;
